// Expiry head of a server-capped list tape (backend livepending.WindowHead).
//
// A list row with more trades in its window than the server ships keeps only
// the newest events (`window_events_tail`). Its window figures (TX, buys,
// sells, volume) were then frozen REST aggregates plus live increments — they
// could only grow. The head carries the dropped events reduced to what expiry
// reads, so the row ages with the clock exactly like an uncapped one:
//
//   - head.events: every dropped event that leaves the window (or the recent
//     quarter) within the server's horizon, in tape order;
//   - head.rest: the other dropped events, summed — all of them stay inside
//     the window (and keep their recent-quarter membership) until head.until;
//   - head.until: unix seconds the head is exact through; 0 = for good.
//
// Wire (window_events_head_v1):
//   [window_sec, until, base_ts, ts_deltas, sides, quote_native, quote_usd, rest]
//   rest = null | [txns, buys, sells, quote_native, quote_usd, usd_unpriced,
//                  recent_native, recent_usd]

import type { CompactWindowHeadV1, WindowHead, WindowTradeEvent } from '../api/types'

export function expandWindowHead(raw: CompactWindowHeadV1, poolAddr: string): WindowHead {
  const [windowSec, until, baseTS, deltas, sides, native, usd, rest] = raw
  const events: WindowTradeEvent[] = []
  let ts = baseTS
  for (let index = 0; index < deltas.length; index++) {
    ts += deltas[index] as number
    const side = sides[index]
    events.push({
      ts,
      // Head events are strictly older than every tail event, so a zero LT
      // keeps tape order; they never take part in trade identity or replay.
      lt: 0,
      tx_hash: '',
      pool_addr: poolAddr,
      side: side === 'b' ? 'buy' : side === 's' ? 'sell' : 'unknown',
      quote_native: native[index] as number,
      quote_usd: usd[index] ?? null,
    })
  }
  return {
    window_sec: windowSec,
    until,
    events,
    rest:
      rest == null
        ? null
        : {
            txns: rest[0],
            buys: rest[1],
            sells: rest[2],
            quote_native: rest[3],
            quote_usd: rest[4],
            usd_unpriced: rest[5],
            recent_native: rest[6],
            recent_usd: rest[7],
          },
  }
}

/** The head is exact through `until` (0 = no horizon). */
export function windowHeadCurrent(head: WindowHead, nowSec: number): boolean {
  return head.until === 0 || nowSec <= head.until
}

const headTapeCache = new WeakMap<
  WindowHead,
  WeakMap<readonly WindowTradeEvent[], WindowTradeEvent[]>
>()

/** head.events followed by the row's retained tape (tail + live trades), one
 *  stable array per (head, tape) pair so the window index is built once. */
export function windowHeadTape(
  head: WindowHead,
  tape: readonly WindowTradeEvent[]
): WindowTradeEvent[] {
  let byTape = headTapeCache.get(head)
  if (!byTape) {
    byTape = new WeakMap()
    headTapeCache.set(head, byTape)
  }
  const cached = byTape.get(tape)
  if (cached) return cached
  const combined = [...head.events, ...tape]
  byTape.set(tape, combined)
  return combined
}

/** Earliest horizon among rows whose head is still current, or null. A feed
 *  re-reads its snapshot when this passes: past it the rest summary is no
 *  longer guaranteed to sit inside the window. */
export function nextWindowHeadHorizon(
  rows: readonly { window_head?: WindowHead | null }[]
): number | null {
  let next: number | null = null
  for (const row of rows) {
    const until = row.window_head?.until
    if (!until) continue
    if (next == null || until < next) next = until
  }
  return next
}

/** One horizon re-read: which `until` it serves, how many attempts so far and
 *  the second the next attempt may start. */
export interface HeadReloadState {
  until: number
  attempts: number
  nextAtSec: number
}

const HEAD_RELOAD_FIRST_BACKOFF_SEC = 5
const HEAD_RELOAD_MAX_BACKOFF_SEC = 60

/**
 * Decides whether a feed re-reads its snapshot now because a head horizon
 * passed. Returns the state to record when it should, null otherwise.
 *
 * A successful re-read brings rows with a later horizon, so `until` changes
 * and the next re-read waits for it. A failed (or still lagging) one leaves
 * the same `until` in place: it is retried after 5 s, 10 s, 20 s … capped at
 * 60 s — never every tick, never given up for the session.
 */
export function headReloadDue(
  state: HeadReloadState | null,
  horizon: number | null,
  nowSec: number
): HeadReloadState | null {
  if (horizon == null || nowSec <= horizon) return null
  if (state == null || state.until !== horizon) {
    return { until: horizon, attempts: 1, nextAtSec: nowSec + HEAD_RELOAD_FIRST_BACKOFF_SEC }
  }
  if (nowSec < state.nextAtSec) return null
  const attempts = state.attempts + 1
  const backoff = Math.min(
    HEAD_RELOAD_MAX_BACKOFF_SEC,
    HEAD_RELOAD_FIRST_BACKOFF_SEC * 2 ** (attempts - 1)
  )
  return { until: horizon, attempts, nextAtSec: nowSec + backoff }
}
